import { TitleCasePipe } from '@angular/common';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { NewEnvelopeService } from 'src/app/envelope/new-envelope/new-envelope.service';
import { NgbModalConfig, NgbModal, NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Authorizer } from '../../model/authorizer';
import { IBlankslate, ToastService, IToast, LayoutService, ITableConfig } from 'codex-lib';
import { TableAuthorizerUser } from '../model/tableAuthorizerUser';
import { Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { NewEnvelopeSteps } from 'src/app/enum/new-envelope-steps';
import { CPFCNPJUtil } from 'src/app/util/CPF-CNPJ-util';
import { AuthService } from 'src/app/services/auth.service';
import { TenantCustomizationService } from 'src/app/services/tenant-customization.service';
import { ModalIntentType } from 'src/app/enum/modal-intent-type.enum';
import { ParticipantTypeEnum } from 'src/app/enum/participant.enum';
import { TrackerData } from 'src/app/modules/google-analytics/model/tracker-data.model';
import { AnalyticEventClient } from 'src/app/enum/analytic-event-client.enum';
import { AnalyticsService } from 'src/app/modules/google-analytics/service/analytics.service';
import { MainEnvelopeComponent } from 'src/app/main-envelope/main-envelope.component';
import { TableSignUser } from '../model/tableSignUser';
import { StringUtil } from 'src/app/util/string-util';
import { EnvelopeDataTableService } from '../services/envelope-data-table.service';

@Component({
  selector: 'envelope-authorizer',
  templateUrl: './envelope-authorizer.component.html',
  styleUrls: ['./envelope-authorizer.component.scss']
})
export class AuthorizerComponent implements OnInit, OnDestroy {

  tableAuthorizerUser: TableAuthorizerUser;
  blankSlate: IBlankslate;
  blankSlateSearch: IBlankslate;
  searchText = '';
  authorizerUsers: Subscription;
  stepModelSubscription: Subscription;
  checkboxAllElements: boolean;
  activeModal: NgbActiveModal;
  settings: any;
  hiddenPhoneColumn: boolean = false;
  logoPath;
  modalIntentType = ModalIntentType;
  participantType = ParticipantTypeEnum;
  dataTableOptions: ITableConfig;

  model: {
    envelopeAuthorizers: Array<Authorizer>;
    authorizerAccessAuthentication: boolean;
  };

  constructor(modalConfig: NgbModalConfig,
    private authService: AuthService,
    private modalService: NgbModal,
    private newEnvelopeService: NewEnvelopeService,
    private translate: TranslateService,
    private toastService: ToastService,
    private layoutService: LayoutService,
    private tenantCustomizationService: TenantCustomizationService,
    private mainEnvelopeComponent: MainEnvelopeComponent,
    private titleCasePipe: TitleCasePipe,
    private googleAnalitycsService: AnalyticsService,
    private envelopeDataTableService: EnvelopeDataTableService) {
    this.model = {
      envelopeAuthorizers: new Array<Authorizer>(),
      authorizerAccessAuthentication: false
    };

    this.hiddenPhoneColumn = !this.mainEnvelopeComponent.isSendWhatsapp;

    this.tableAuthorizerUser = new TableAuthorizerUser();
    this.checkboxAllElements = false;
    this.authorizerUsers = this.tableAuthorizerUser.authorizerUsers.subscribe(x => {
      this.model.envelopeAuthorizers = x;
      this.model.envelopeAuthorizers.forEach(e => {
        e.ssnWithMask = e.ssn ? CPFCNPJUtil.obfuscateCpfCnpj(CPFCNPJUtil.formatSsn(e.ssn)) : '';
      });
      this.model = this.newEnvelopeService.setModelData(this.model);
      this.validateAllCheckboxCheckedTable();
      this.refreshDataTableOptions();
    });

    this.stepModelSubscription = this.newEnvelopeService.stepModelSubject.subscribe(modelData => {
      this.model = modelData;
    });

    if (!this.authService.signUserModel) {
      return;
    }

    modalConfig.backdrop = 'static';
    modalConfig.keyboard = false;
  }

  public trackById(index: number, item: Authorizer) {
    return item?.id ?? index;
  }

  ngOnDestroy(): void {
    this.authorizerUsers.unsubscribe();
    this.stepModelSubscription.unsubscribe();
  }

  ngOnInit() {

    this.tenantCustomizationService.tenantLogo.subscribe(imgLogo =>{
      this.layoutService.logoProp.next({ img: imgLogo });
      this.logoPath = imgLogo;
    });

    this.settings = this.newEnvelopeService.getModelData(NewEnvelopeSteps.Settings).value;

    Object.assign(this.model, this.newEnvelopeService.getModelData(this.newEnvelopeService.getCurrentStep()));
    this.tableAuthorizerUser.addList(this.model.envelopeAuthorizers);

    this.blankSlate = {
      icon: {
        prefix: 'fad',
        iconName: 'user-plus'
      },
      buttonText: 'add'
    };

    this.blankSlateSearch = {
      icon: {
        prefix: 'fad',
        iconName: 'search-minus'
      },
      buttonText: 'cleanSearch'
    };
    this.refreshDataTableOptions();
  }

  private refreshDataTableOptions(): void {
      const envelopeAuthorizers = this.model.envelopeAuthorizers || [];
      const selectedCount = envelopeAuthorizers.filter((element) => !!element[TableAuthorizerUser.propCheck]).length;
      const allSelected = envelopeAuthorizers.length > 0 && selectedCount === envelopeAuthorizers.length;
      const partiallySelected = selectedCount > 0 && !allSelected;

      const headers = this.envelopeDataTableService.buildHeader(
        [
          { text: this.titleCasePipe.transform(this.translate.instant('name')), show: true },
          { text: this.titleCasePipe.transform(this.translate.instant('mail')), show: true },
          { text: this.translate.instant('ssn').toUpperCase(), show: this.settings?.language?.id === 1 },
          { text: this.translate.instant('phone'), show: !this.hiddenPhoneColumn }
        ]
      );
      
      const body = this.envelopeDataTableService.buildBody(
        envelopeAuthorizers.map((authorizer) => ({
          row: 
          [
            { display: authorizer.name },
            { display: StringUtil.obfuscateMail(authorizer.mail) || '-' },
            { display: authorizer.ssnWithMask || '-' },
            { display: StringUtil.obfuscateTelephone(authorizer.phoneNumber) || '-' }
          ],
          metaData: { isSelected: !!authorizer[TableAuthorizerUser.propCheck] },
        }))
      );

      this.checkboxAllElements = allSelected;

      this.dataTableOptions = this.envelopeDataTableService.refreshDataTableOptions(headers, body, {
        isSelectable: true,
        isTableFull: true,
        allSelected,
        partiallySelected
      });
    }
  
    onDataTableChange(event: { allSelected?: boolean; partiallySelected?: boolean }): void {
      if (!this.dataTableOptions?.body?.length) {
        return;
      }
  
      this.model.envelopeAuthorizers.forEach((element, index) => {
        const isSelected = !!this.dataTableOptions.body[index]?.metaData?.isSelected;
        element[TableAuthorizerUser.propCheck] = isSelected;
      });
  
      this.checkboxAllElements = !!event?.allSelected;
      this.dataTableOptions = {
        ...this.dataTableOptions,
        allSelected: !!event?.allSelected,
        partiallySelected: !!event?.partiallySelected,
      };
  
      this.newEnvelopeService.setModelData(this.model);
    }

  confirmModalClose() {
    this.activeModal.dismiss();
  }

  confirmModalYesClick() {
    this.removeAuthorizer();
    this.activeModal.dismiss();
  }

  showConfirmationDelete(confirmModalContent) {
    this.activeModal = this.modalService.open(confirmModalContent);
  }

  removeAuthorizer() {
    const filterElements = Object.assign([], this.model.envelopeAuthorizers);
    filterElements.forEach(el => {
      if (el[TableAuthorizerUser.propCheck]) {
        this.tableAuthorizerUser.removeEl(el);
      }
    });
    this.searchText = '';
    this.checkboxAllElements = false;
    this.refreshDataTableOptions();
  }

  validateAllCheckboxCheckedTable() {
    let allCheked = true;
    allCheked = this.model.envelopeAuthorizers.every(element => element[TableAuthorizerUser.propCheck]);
    if (allCheked || this.checkboxAllElements) {
      this.checkboxAllElements = allCheked;
    }
  }

  onCheckbox(ev, el) {
    const obj = {};
    obj[TableAuthorizerUser.propCheck] = ev;
    this.tableAuthorizerUser.updateElement(el, obj);
    this.searchText = '';
  }

  onCheckboxAll(ev) {
    this.checkboxAllElements = ev;
    const obj = {};
    obj[TableAuthorizerUser.propCheck] = ev;
    this.tableAuthorizerUser.updateElements(obj);
  }

  onSearch(ev) {
    this.searchText = ev;
    this.tableAuthorizerUser.getList(this.searchText);
  }

  anyCheckedElement(): boolean {
    return this.model.envelopeAuthorizers.anyChecked(TableAuthorizerUser.propCheck);
  }

  showAddAuthorizerModal(modalContent) {
    this.activeModal = this.modalService.open(modalContent, { size: 'xl', scrollable: true });
  }

  closeModalClick() {
    this.activeModal.dismiss();
  }

  submitAddModalClick(event: { data: any, isEdited: boolean }) {
    if (event.isEdited){
      this.tableAuthorizerUser.updateByElementId(event.data);
    }else{
      if(!Array.isArray(event.data))
        return;
      
      event.data.map(x => x[TableAuthorizerUser.propCheck] = false);
      const countNewUsers = this.tableAuthorizerUser.mergeList(event.data);
      this.activeModal.close();

      if (countNewUsers > 0) {
        const toastUsersAdded: IToast = {
          text: this.translate.instant('envelope.authorizer.toast.addedNotification', { count: countNewUsers }),
          autoHide: true
        };
        this.toastService.createNewToast(toastUsersAdded);
      }
    }
    this.refreshDataTableOptions();
  }

  onBlankSlateSearchClick() {
    this.onSearch('');
  }

  onToggleAccessAuthentication(event) {
    this.googleAnalitycsService.sendEvent(
          new TrackerData(
            AnalyticEventClient.TK_PT_SIGNER_NEW_ENVELOPE,
            'Signer_NovEnv_AutenticaçãoAprovadores',
            event ? 'Ativo' : 'Inativo'
          )
        );

    this.model.authorizerAccessAuthentication = event;
  }
}
